import { useEffect, useState, useRef } from 'react';
import { useQuery } from 'react-query';
import { useParams, useLocation, useNavigationType } from 'react-router-dom';
import { getItemGroupPageData } from '../../../section/CMS/api/cmsApi';

import TextHeader from '../../textHeader/TextHeader';
import FilterComponent from '../filter/FilterComponent';
import LoadingScreen from '../loading/LoadingScreen';
import MissingPage from '../errorMessages/MissingPage';
import { captureAnalytics } from '../../../utilities/Analytics';
import { CMSPageData} from '../../../section/CMS/types/CMSTypes';

// Import new CMS components
import CMSConsumerCarouselBanner from '../../../section/CMS/components/banner/CMSConsumerCarouselBanner';
import CMSConsumerApplicationsGrid from '../../../section/CMS/components/grid/CMSConsumerApplicationsGrid';
import CMSConsumerFeaturedGallery from '../../../section/CMS/components/gallery/CMSConsumerFeaturedGallery';
import CMSConsumerNewArrivalsGrid from '../../../section/CMS/components/grid/CMSConsumerNewArrivalsGrid';

function CategoryTemplate() {
  const { item_group } = useParams();
  const location = useLocation();
  const navigationType = useNavigationType();

  // Create scroll position management
  const scrollPositionKey = `category_scroll_${item_group}`;
  const scrollRestored = useRef(false);

  // CMS Data Query - Consumer always uses live mode
  const {
    isSuccess: cmsSuccess,
    isError: cmsError,
    isLoading: cmsLoading,
    data: cmsResponse,
    refetch: cmsRefetch
  } = useQuery(
    [`cms-consumer-item-group-${item_group}`],
    () => getItemGroupPageData(item_group || '', 'live'), // Always use live mode for consumer
    {
      staleTime: 600000, // Cache data for 10 minutes
      enabled: !!item_group, // Only run if item_group exists
    }
  );

  // Get the CMS data (always use live data for consumer)
  const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;

  // Get the children data (applications/categories) - always use active data
  const childrenData = cmsData?.children_tile_data?.active || [];

  // Get the new arrivals data and filter published items only
  const rawNewArrivalsData = cmsData?.new_arrivals?.active || [];
  const newArrivalsData = rawNewArrivalsData
    .filter(item => item.is_published) // Consumer only sees published items
    .sort((a, b) => {
      const orderA = a.order_position || 999;
      const orderB = b.order_position || 999;
      return orderA - orderB;
    });

  // Check if this is homeware (item_group 000)
  const isHomeware = item_group === '000';

  // Fixed scroll management - only scroll to top on new navigations, not when returning
  useEffect(() => {
    if (navigationType !== 'POP') {
      // New navigation, scroll to top and clear saved position
      window.scrollTo(0, 0);
      sessionStorage.removeItem(scrollPositionKey);
      scrollRestored.current = false;
    } else if (!scrollRestored.current) {
      // Coming back, try to restore position only once
      const savedPosition = sessionStorage.getItem(scrollPositionKey);
      if (savedPosition) {
        const targetPosition = parseInt(savedPosition, 10);

        // Wait for content to load before restoring scroll
        const restoreScroll = () => {
          window.scrollTo(0, targetPosition);
          scrollRestored.current = true;
        };

        // Try immediate restore
        restoreScroll();

        // Also try after content loads
        const timeoutId = setTimeout(restoreScroll, 100);
        const finalTimeout = setTimeout(restoreScroll, 500);

        return () => {
          clearTimeout(timeoutId);
          clearTimeout(finalTimeout);
        };
      } else {
        // No saved position, ensure we're at top
        window.scrollTo(0, 0);
        scrollRestored.current = true;
      }
    }
  }, [navigationType, scrollPositionKey, cmsSuccess]);

  // Save scroll position when leaving the page - only save if we're not restoring
  useEffect(() => {
    const handleScroll = () => {
      // Only save scroll position if we're not in the middle of restoring
      if (scrollRestored.current) {
        sessionStorage.setItem(scrollPositionKey, window.scrollY.toString());
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollPositionKey]);

  // Handle window resize events - more conservative approach
  useEffect(() => {
    const handleResize = () => {
      // Only restore on resize if we have a saved position and have successfully restored before
      if (scrollRestored.current && navigationType === 'POP') {
        const savedPosition = sessionStorage.getItem(scrollPositionKey);
        if (savedPosition) {
          window.scrollTo(0, parseInt(savedPosition, 10));
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [scrollPositionKey, navigationType]);

  // Analytics tracking
  useEffect(() => {
    if (cmsSuccess && cmsData) {
      const title = cmsData.page_title;
      captureAnalytics('pageview', location.pathname, title);
    }
  }, [location.pathname, item_group, cmsSuccess, cmsData]);

  // Determine loading and error states
  const isLoading = cmsLoading;
  const isError = cmsError;
  const isSuccess = cmsSuccess;

  return (
    <>
      {isLoading && <LoadingScreen />}
      {isError && <MissingPage />}
      {isSuccess && (
        <>
          {/* CMS Consumer Carousel Banner */}
          {cmsData && (
            <CMSConsumerCarouselBanner
              bannerMediaOption={cmsData.consumer_banner_media_option}
              bannerMediaCarousel={cmsData.consumer_banner_media_carousel || []}
              bannerMediaVideo={cmsData.consumer_banner_media_video}
              bannerMediaVideoLink={cmsData.consumer_banner_media_video_link}
              bannerLinkCategoryId={cmsData.consumer_banner_link_category_id}
              bannerWidth={cmsData.consumer_banner_width}
              bannerHeight={cmsData.consumer_banner_height}
              viewMode="live"
            />
          )}

          {/* Page Header */}
          <TextHeader
            heading={cmsData?.page_title || 'Loading...'}
            content={cmsData?.description || ''}
            isCentered={false}
            width='70%'
            marginTop={10}
            marginBottom={5}
          />

          {/* CMS Consumer Applications Grid - Replaces old ApplicationsGrid and HomewareCategoryGrid */}
          <CMSConsumerApplicationsGrid
            childrenData={childrenData}
            spacingValue={4}
            marginTop={15}
            marginBottom={15}
            isHomeware={isHomeware}
          />

          {/* CMS Consumer Featured Gallery - Only show if enabled and has data */}
          {cmsData?.featured_collection_display_switch && cmsData?.featured_collection && (
            <CMSConsumerFeaturedGallery
              featuredCollection={cmsData.featured_collection}
              hasVideo={false}
              invert={false}
              marginTop={5}
              marginBottom={5}
              viewMode="live"
            />
          )}

          {/* CMS Consumer New Arrivals - Only show if has data */}
          {newArrivalsData.length > 0 && (
            <CMSConsumerNewArrivalsGrid
              newArrivalsData={newArrivalsData}
              header="New arrivals"
              hasTitles={true}
              hasButtons={true}
              hasBackgroundText={false}
              spacingValue={4}
              marginTop={'5rem'}
              marginBottom={'5rem'}
              viewMode="live"
            />
          )}

          {/* Filter Component - Only show for non-homeware categories */}
          {item_group !== '000' && (
            <FilterComponent
              itemGroup={item_group}
              title={cmsData?.page_title || ''}
              initialApplications={[]}
            />
          )}
        </>
      )}
    </>
  );
}

export default CategoryTemplate;